import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Newspaper, Pencil, Plus, Trash2, UploadCloud, X, EyeOff, Eye } from "lucide-react";
import { API, fileSrc } from "@/lib/api";

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors text-sm";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", published: true };

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/[\s-]+/g, "-")
    .slice(0, 80);

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("ka-GE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
};

export default function AdminBlog({ auth }) {
  const [posts, setPosts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const coverInput = useRef(null);

  const load = () => axios.get(`${API}/admin/blog`, auth).then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startAdd = () => {
    setForm(EMPTY);
    setSlugTouched(false);
    setEditingId(null);
    setAdding(true);
  };

  const startEdit = (p) => {
    setForm({ title: p.title, slug: p.slug, excerpt: p.excerpt, content: p.content, published: p.published });
    setSlugTouched(true);
    setAdding(false);
    setEditingId(p.id);
  };

  const cancel = () => {
    setAdding(false);
    setEditingId(null);
    setForm(EMPTY);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, slug: form.slug || slugify(form.title) };
    if (!payload.slug) {
      toast.error("შეავსე სათაური ან ბმული (slug)");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const r = await axios.put(`${API}/blog/${editingId}`, payload, auth);
        setPosts((prev) => prev.map((p) => (p.id === editingId ? r.data : p)));
        toast.success("სტატია განახლდა");
      } else {
        const r = await axios.post(`${API}/blog`, payload, auth);
        setPosts((prev) => [r.data, ...prev]);
        toast.success("სტატია დაემატა");
        setEditingId(r.data.id);
        setSlugTouched(true);
        setAdding(false);
        return;
      }
      cancel();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "შენახვა ვერ მოხერხდა");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`ნამდვილად წაიშალოს „${p.title}"?`)) return;
    try {
      await axios.delete(`${API}/blog/${p.id}`, auth);
      setPosts((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("სტატია წაიშალა");
    } catch {
      toast.error("წაშლა ვერ მოხერხდა");
    }
  };

  const togglePublished = async (p) => {
    try {
      const r = await axios.put(`${API}/blog/${p.id}`, { title: p.title, slug: p.slug, excerpt: p.excerpt, content: p.content, published: !p.published }, auth);
      setPosts((prev) => prev.map((x) => (x.id === p.id ? r.data : x)));
      toast.success(r.data.published ? "სტატია გამოქვეყნდა" : "სტატია დამალულია");
    } catch {
      toast.error("ოპერაცია ვერ მოხერხდა");
    }
  };

  const uploadCover = async (file) => {
    if (!file || !editingId) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await axios.post(`${API}/blog/${editingId}/cover`, fd, auth);
      setPosts((prev) => prev.map((x) => (x.id === editingId ? { ...x, cover_url: r.data.cover_url } : x)));
      toast.success("ყდის სურათი აიტვირთა");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "ატვირთვა ვერ მოხერხდა");
    } finally {
      setUploading(false);
      if (coverInput.current) coverInput.current.value = "";
    }
  };

  const editing = posts.find((p) => p.id === editingId);
  const showForm = adding || editingId;

  return (
    <div data-testid="admin-blog-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">ბლოგი ({posts.length})</h2>
        <button onClick={adding ? cancel : startAdd} data-testid="admin-add-post-btn" className="btn-gold !py-2.5 !px-5 text-sm">
          {adding ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {adding ? "დახურვა" : "ახალი სტატია"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="card-lux p-6 mb-8 space-y-4" data-testid="admin-post-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">სათაური <span className="text-[#D4AF37]">*</span></label>
              <input
                data-testid="admin-post-title"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value, slug: slugTouched ? form.slug : slugify(e.target.value) })}
                className={inputCls}
                placeholder="მაგ. როგორ დაზოგო საწვავი ტაქსში"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">ბმული (slug) — მხოლოდ ლათინური, მაგ. savfavis-dazogva</label>
              <input
                data-testid="admin-post-slug"
                required
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value.toLowerCase() }); }}
                className={inputCls}
                placeholder="savfavis-dazogva"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">მოკლე აღწერა (SEO და სიისთვის)</label>
            <textarea data-testid="admin-post-excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={`${inputCls} resize-none`} />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">ტექსტი — „## " სათაურისთვის, „- " სიისთვის, ცარიელი ხაზი აბზაცებს ჰყოფს</label>
            <textarea data-testid="admin-post-content" rows={10} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={`${inputCls} resize-y font-mono`} />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" data-testid="admin-post-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" />
              გამოქვეყნებული
            </label>
            <button type="submit" disabled={saving} data-testid="admin-post-save" className="btn-gold !py-2.5 !px-6 text-sm disabled:opacity-60">
              {saving ? "ინახება..." : "შენახვა"}
            </button>
            <button type="button" onClick={cancel} data-testid="admin-post-cancel" className="btn-ghost !py-2.5 !px-6 text-sm">
              გაუქმება
            </button>
          </div>
          {editingId && (
            <div className="pt-4 border-t border-white/10 flex items-center gap-4">
              <div className="h-20 w-32 rounded-xl overflow-hidden bg-[#0D0F14] border border-white/10">
                {editing?.cover_url ? (
                  <img src={fileSrc(editing.cover_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-600"><Newspaper className="w-5 h-5" /></div>
                )}
              </div>
              <button type="button" onClick={() => coverInput.current?.click()} disabled={uploading} data-testid="admin-post-cover-btn" className="btn-ghost !py-2 !px-4 text-xs">
                <UploadCloud className="w-3.5 h-3.5" /> {uploading ? "იტვირთება..." : "ყდის სურათი"}
              </button>
              <input type="file" accept="image/*" hidden ref={coverInput} data-testid="admin-post-cover-input" onChange={(e) => uploadCover(e.target.files[0])} />
              <span className="text-xs text-gray-500">ყდა ხვდება სიაში, სტატიაში და სოციალური გაზიარებისთვის</span>
            </div>
          )}
        </form>
      )}

      {posts.length === 0 && !showForm ? (
        <div className="border border-dashed border-white/20 rounded-xl p-12 text-center text-gray-500" data-testid="admin-blog-empty">
          სტატიები ჯერ არ არის — დაამატე პირველი
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-blog-list">
          {posts.map((p) => (
            <div key={p.id} className="card-lux p-4 flex items-center gap-4" data-testid={`admin-post-${p.id}`}>
              <div className="w-20 h-14 rounded-lg overflow-hidden bg-[#0D0F14] border border-white/10 shrink-0">
                {p.cover_url && <img src={fileSrc(p.cover_url)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{p.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  /blog/{p.slug} · {fmtDate(p.created_at)}
                  {!p.published && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/10 text-gray-400 border border-white/20">დამალული</span>}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => togglePublished(p)} data-testid={`admin-post-publish-${p.id}`} className="btn-ghost !py-2 !px-3.5 text-xs">
                  {p.published ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => startEdit(p)} data-testid={`admin-post-edit-${p.id}`} className="btn-ghost !py-2 !px-3.5 text-xs">
                  <Pencil className="w-3.5 h-3.5" /> რედაქტირება
                </button>
                <button onClick={() => remove(p)} data-testid={`admin-post-delete-${p.id}`} className="!py-2 !px-3.5 text-xs inline-flex items-center gap-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
